import { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AnimatePresence } from 'framer-motion'
import Layout from '@/components/Layout'
import LoadingScreen from '@/components/LoadingScreen'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import ScrollToTop from '@/components/ScrollToTop'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { useAppStore } from '@/services/store'
// Removed legacy creation providers
import { AuthProvider, useAuth } from '@/contexts/AuthContext'
import { OnboardingProvider } from '@/contexts/OnboardingContext'
import { UserProfileProvider } from '@/contexts/UserProfileContext'
import { getCurrentUserProfile } from './services/userProfile'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/features/home/HomePage'))
const ManageProfilesPage = lazy(() => import('@/features/profiles/ManageProfilesPage'))
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'))

// Settings pages
const StorytellingPreferencesPage = lazy(
  () => import('@/features/settings/StorytellingPreferencesPage')
)
const LanguageSettingsPage = lazy(() => import('@/features/settings/LanguageSettingsPage'))
const RemindersPage = lazy(() => import('@/features/settings/RemindersPage'))
const SubscriptionPage = lazy(() => import('@/features/settings/SubscriptionPage'))

// Auth pages
const LoginPage = lazy(() => import('@/features/auth/LoginPage'))
const SignupPage = lazy(() => import('@/features/auth/SignupPage'))
const ForgotPasswordPage = lazy(() => import('@/features/auth/ForgotPasswordPage'))

// Onboarding pages
const OnboardingWizardController = lazy(
  () => import('@/features/onboarding/OnboardingWizardController')
)
// const CaregiverInfoPage = lazy(() => import('@/features/onboarding/CaregiverInfoPage'))
// const ChildrenInfoPage = lazy(() => import('@/features/onboarding/ChildrenInfoPage'))
// const AvatarSelectionPage = lazy(() => import('@/features/onboarding/AvatarSelectionPage'))
// Superpower flow removed

// Realms
const StoriesRealmPage = lazy(() => import('@/features/realms/StoriesRealmPage'))
const ExploreRealmPage = lazy(() => import('@/features/realms/ExploreRealmPage'))
const AnimateRealmPage = lazy(() => import('@/features/realms/AnimateRealmPage'))
const AnimateCreatePage = lazy(() => import('@/features/realms/AnimateCreatePage'))
const AnimateFromStoryPage = lazy(() => import('@/features/realms/AnimateFromStoryPage'))
const CreativityRealmPage = lazy(() => import('@/features/realms/CreativityRealmPage'))
const MyCreationsPage = lazy(() => import('@/features/library/MyCreationsPage'))
const CommunityLibraryPage = lazy(() => import('@/features/library/CommunityLibraryPage'))
const StoryReaderPage = lazy(() => import('@/features/stories/StoryReaderPage'))

function AppRoutes() {
  const { user, onboardingCompleted, onboardingLoading, loading: authLoading } = useAuth()

  // Show loading screen while checking onboarding status for authenticated users
  if ((authLoading && !user) || (user && onboardingLoading)) {
    return <LoadingScreen />
  }
 
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/auth/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/auth/signup" element={user ? <Navigate to="/" replace /> : <SignupPage />} />
      <Route
        path="/auth/forgot-password"
        element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />}
      />

      {/* Onboarding routes */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizardController />
          </ProtectedRoute>
        }
      />
      {/* Legacy onboarding routes for backward compatibility */}
      <Route path="/onboarding/caregiver" element={<Navigate to="/onboarding" />} />
      <Route path="/onboarding/children" element={<Navigate to="/onboarding" />} />
      <Route path="/onboarding/avatars" element={<Navigate to="/onboarding" />} />

      {/* Superpower flow removed */}

      {/* Realms */}
      <Route
        path="/realms/stories"
        element={
          <ProtectedRoute>
            <StoriesRealmPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realms/explore"
        element={
          <ProtectedRoute>
            <ExploreRealmPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realms/animate"
        element={
          <ProtectedRoute>
            <AnimateRealmPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realms/animate/from-story"
        element={
          <ProtectedRoute>
            <AnimateFromStoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realms/animate/create"
        element={
          <ProtectedRoute>
            <AnimateCreatePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/realms/creativity"
        element={
          <ProtectedRoute>
            <CreativityRealmPage />
          </ProtectedRoute>
        }
      />

      {/* Library */}
      <Route
        path="/library/my"
        element={
          <ProtectedRoute>
            <MyCreationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/library/community"
        element={
          <ProtectedRoute>
            <CommunityLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/story/:id"
        element={
          <ProtectedRoute>
            <StoryReaderPage />
          </ProtectedRoute>
        }
      />

      {/* Entry route - send unauthenticated users to login, others through protection */}
      <Route
        path="/"
        element={
          user ? (
            <ProtectedRoute>
              {onboardingCompleted ? <HomePage /> : <Navigate to="/onboarding" replace />}
            </ProtectedRoute>
          ) : (
            // <ProtectedRoute>{<HomePage />}</ProtectedRoute>
            <Navigate to="/auth/login" replace />
          )
        }
      />
      <Route
        path="/profiles"
        element={
          <ProtectedRoute>
            <ManageProfilesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profiles/new"
        element={
          <ProtectedRoute>
            <ManageProfilesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/storytelling"
        element={
          <ProtectedRoute>
            <StorytellingPreferencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/language"
        element={
          <ProtectedRoute>
            <LanguageSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/reminders"
        element={
          <ProtectedRoute>
            <RemindersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

function App() {
  const { isOnline } = useNetworkStatus()
  const { theme, setTheme } = useAppStore()

  useEffect(() => {
    // Set initial theme based on system preference if no saved preference
    if (!localStorage.getItem('story-tales-storage')) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      setTheme(systemTheme)
    }
  }, [])

  return (
    // <div className={`app ${theme}`}>
    <div className={`app light`}>
      <ErrorBoundary>
        <AuthProvider>
          <UserProfileProvider>
            <OnboardingProvider>
              <Layout>
                <ScrollToTop />
                <AnimatePresence mode="wait">
                  <Suspense fallback={<LoadingScreen />}>
                    <AppRoutes />
                  </Suspense>
                </AnimatePresence>
              </Layout>
            </OnboardingProvider>
          </UserProfileProvider>
        </AuthProvider>
      </ErrorBoundary>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: theme === 'dark' ? '#1f2937' : '#ffffff',
            color: theme === 'dark' ? '#f3f4f6' : '#111827',
          },
        }}
      />

      {!isOnline && (
        <div className="fixed bottom-4 left-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg text-center">
          You're offline - some features may be limited
        </div>
      )}
    </div>
  )
}

export default App
